import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence, useScroll } from "framer-motion";
import { ThemeSwitch } from "./ThemeSwitch";
import { track } from "../lib/analytics";
import { SITE } from "../config";

const links = [
  { to: "/#how-it-works", label: "How It Works" },
  { to: "/features", label: "Features" },
  { to: "/intention", label: "Discover Your Intention" },
  { to: "/experience", label: "Experience" },
  { to: "/slm", label: "SLM Technology" },
  { to: "/about", label: "About" },
];

const Logo = ({ className = "" }) => (
  <span className={`brand-lockup gap-2 ${className}`}>
    <img src="/brand/humn-mark.png" alt="" aria-hidden="true" className="h-6 sm:h-7 w-auto" />
    <img src="/brand/humn-wordmark.png" alt="HUMN" className="h-4 sm:h-[18px] w-auto" />
  </span>
);

export const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [menu, setMenu] = useState(false);
  const { scrollY } = useScroll();

  useEffect(() => {
    let last = 0;
    return scrollY.on("change", (y) => {
      setScrolled(y > 40);
      setHidden(y > 500 && y > last && !menu);
      last = y;
    });
  }, [scrollY, menu]);

  useEffect(() => {
    document.body.style.overflow = menu ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menu]);

  return (
    <>
      <motion.header
        animate={{ y: hidden ? -100 : 0, scale: scrolled && !hidden ? 0.985 : 1 }}
        transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
        className="fixed top-5 left-1/2 z-50 w-[min(94vw,1180px)]"
        style={{ x: "-50%" }}
      >
        <nav
          aria-label="Main navigation"
          className={`flex items-center justify-between rounded-full px-5 sm:px-6 py-3 transition-all duration-500 ${scrolled ? "glass-3 glass-reflect" : "glass-1"}`}
        >
          <Link to="/" data-testid="nav-logo-link" aria-label="HUMN home" className="shrink-0 relative z-10">
            <Logo />
          </Link>
          <div className="hidden lg:flex items-center gap-3 xl:gap-5 relative z-10">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
                className={({ isActive }) => `text-[12px] xl:text-[13px] tracking-tight transition-colors duration-200 ${isActive ? "tx-1 font-semibold" : "tx-2 hover:opacity-100"}`}
              >
                {l.label}
              </NavLink>
            ))}
          </div>
          <div className="flex items-center gap-3 relative z-10">
            <ThemeSwitch />
            <a
              href={SITE.googlePlayUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="nav-download-btn"
              onClick={() => track("nav_download_clicked")}
              className="btn-cta hidden sm:block text-[13px] font-semibold px-5 py-2.5"
            >
              Download
            </a>
            <button
              data-testid="nav-menu-btn"
              aria-label={menu ? "Close menu" : "Open menu"}
              aria-expanded={menu}
              onClick={() => setMenu(!menu)}
              className="lg:hidden w-9 h-9 flex flex-col items-center justify-center gap-1.5"
            >
              <motion.span animate={{ rotate: menu ? 45 : 0, y: menu ? 4 : 0 }} className="block w-5 h-[1.5px]" style={{ background: "var(--text-1)" }} />
              <motion.span animate={{ rotate: menu ? -45 : 0, y: menu ? -4 : 0 }} className="block w-5 h-[1.5px]" style={{ background: "var(--text-1)" }} />
            </button>
          </div>
        </nav>
      </motion.header>

      <AnimatePresence>
        {menu && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 scene scene-aurora flex flex-col justify-center px-10"
            style={{ background: "var(--bg-primary)" }}
            data-testid="mobile-menu"
          >
            <div className="space-y-6 relative z-10">
              {[{ to: "/", label: "Home" }, ...links].map((l, i) => (
                <motion.div key={l.to} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 + i * 0.06 }}>
                  <Link
                    to={l.to}
                    data-testid={`mobile-menu-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
                    onClick={() => setMenu(false)}
                    className="h-display text-4xl block"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <motion.a href={SITE.googlePlayUrl} target="_blank" rel="noreferrer" data-testid="mobile-menu-download-btn" onClick={() => setMenu(false)} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .5 }} className="btn-cta block text-center px-6 py-3 mt-8">Download on Google Play</motion.a>
            </div>
            <p className="eyebrow mt-16 relative z-10 flex items-center gap-2.5">
              <img src="/brand/humn-mark.png" alt="" aria-hidden="true" className="h-5 w-auto" />
              The Ai Social Network
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
